/**
 * CrmSellerFilter - Filtro de vendedor para dashboard CRM
 * 
 * Permite selecionar um vendedor ou "Todos".
 * Suporta tamanho compacto via className.
 * 
 * @author Sistema
 * @version 1.2.0 - Suporte a prop label customizável (default: "Vendedores")
 * RELEASE: 2026-10-05 v1.3.0 - Gatilho do filtro segue o modelo de pill: sem a linha cinza da borda, fundo de superfície, sombra sutil e cantos arredondados (rounded-full).
 */

import React, { useState } from 'react';
import { Check, ChevronDown, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { useCrmSellers } from '@/hooks/useCrmSellers';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * Props do componente CrmSellerFilter
 * @param value - ID do vendedor selecionado (null = todos)
 * @param onChange - Callback para mudança de seleção
 * @param className - Classes CSS adicionais (suporta h-[22px] para versão compacta)
 */
interface CrmSellerFilterProps {
  value: string | null;
  onChange: (sellerId: string | null) => void;
  className?: string;
  /** Rótulo exibido quando nenhum vendedor selecionado (padrão: "Vendedores") */
  label?: string;
}

/**
 * Componente de filtro de vendedor
 */
export function CrmSellerFilter({
  value,
  onChange,
  className,
  label = 'Vendedores',
}: CrmSellerFilterProps) {
  const [open, setOpen] = useState(false);
  const { activeSellers, isLoading } = useCrmSellers();

  // Vendedor selecionado
  const selectedSeller = activeSellers.find(s => s.id === value);

  // Handler para seleção
  const handleSelect = (sellerId: string | null) => {
    onChange(sellerId);
    setOpen(false);
  };

  // Detecta se é versão compacta baseado na classe
  const isCompact = className?.includes('h-[22px]');

  if (isLoading) {
    return <Skeleton className={cn(isCompact ? 'h-[22px] w-[100px]' : 'h-9 w-[160px]')} />;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'h-9 px-3 gap-2 font-normal border-0 bg-background rounded-full',
            className
          )}
        >
          {selectedSeller ? (
            <>
              <SmartAvatar
                src={selectedSeller.avatar_url}
                name={selectedSeller.name}
                size="xs"
                use3DFallback
                className={isCompact ? 'h-4 w-4' : undefined}
              />
              <span className={cn('truncate hidden sm:inline', isCompact ? 'max-w-[60px]' : 'max-w-[100px]')}>{selectedSeller.name}</span>
            </>
          ) : (
            <>
              <Users className={cn(isCompact ? 'h-3 w-3' : 'h-4 w-4', 'opacity-70')} />
              <span className="hidden sm:inline truncate">{label}</span>
            </>
          )}
          <ChevronDown className={cn(isCompact ? 'h-2.5 w-2.5' : 'h-3 w-3', 'opacity-50')} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar vendedor..." />
          <CommandList>
            <CommandEmpty>Nenhum vendedor encontrado.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__all__"
                onSelect={() => handleSelect(null)}
                className="cursor-pointer"
              >
                <div
                  className={cn(
                    "mr-2 h-4 w-4 rounded-full border flex items-center justify-center",
                    value === null
                      ? "border-primary bg-primary"
                      : "border-muted"
                  )}
                >
                  {value === null && (
                    <Check className="h-2.5 w-2.5 text-primary-foreground" />
                  )}
                </div>
                <Users className="h-4 w-4 mr-2 opacity-50" />
                <span>Todos Vendedores</span>
              </CommandItem>
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup heading="Vendedores">
              {activeSellers.map(seller => (
                <CommandItem
                  key={seller.id}
                  value={seller.name}
                  onSelect={() => handleSelect(seller.id)}
                  className="cursor-pointer"
                >
                  <div
                    className={cn(
                      "mr-2 h-4 w-4 rounded-full border flex items-center justify-center",
                      value === seller.id
                        ? "border-primary bg-primary"
                        : "border-muted"
                    )}
                  >
                    {value === seller.id && (
                      <Check className="h-2.5 w-2.5 text-primary-foreground" />
                    )}
                  </div>
                  <SmartAvatar
                    src={seller.avatar_url}
                    name={seller.name}
                    size="sm"
                    use3DFallback
                    className="mr-2"
                  />
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="truncate">{seller.name}</span>
                    {seller.email && (
                      <span className="text-xs text-muted-foreground truncate">
                        {seller.email}
                      </span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
